import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { dialog, i18N } from '@/common/util'
import empresasService from '@/services/empresas.service'

import type { EmpresasType } from '@/types/types'

type PeopleAliareType = {
  documentoIdentificacao: string
  localizado: boolean
  nome: string
  telefone: string
  tipo: string
  cadastroAutorizado: boolean
  mensagem: string
}

export const useEmpresasStore = defineStore('empresas', () => {
  /**
   * States
   */
  const list = ref<EmpresasType[]>([])
  const status = ref(0) // -1 erro de exceção; 0 - inicial; 1 - carregando; 2 - sucesso; 3 - erro não fatal

  /**
   * Getters
  */
  const loading = computed(() => status.value === 1)
  const companiesNoContract = computed(() => list.value.filter(el => !el.contratoId))

  /**
   * Actions
   */
  const create = async (objData: EmpresasType) => {
    status.value = 1
    try {
      const { data, success } = await empresasService.create(objData)
      if (success) getAll()

      return data
    } catch {
      //
    } finally {
      status.value = 2
    }
  }

  const update = async (objData: EmpresasType, id: string) => {
    status.value = 1
    try {
      const { data, success } = await empresasService.update<EmpresasType>(id, objData)
      if (success) getAll()

      return data
    } catch {
      //
    } finally {
      status.value = 2
    }
  }

  const getAll = async (): Promise<void> => {
    status.value = 1
    try {
      const result = await empresasService.get()
      list.value = result.data as EmpresasType[]
    } catch {
      //
    } finally {
      status.value = 2
    }
  }

  const remove = async (empresa: EmpresasType, force?: boolean) => {
    if(!force) 
      return dialog.open({
        title: i18N('feedback.attention'),
        message: i18N('dialogs.delete.message', {type: i18N('labels.company').toLowerCase(), name: empresa.nome}),
        complete: () => remove(empresa, true)
      })
    
    try {
      status.value = 1

      const { success } = await empresasService.remove(empresa.id)
      if (success) list.value.splice(list.value.findIndex(el => el.id === empresa.id), 1)

      status.value = 2
      return success
    } catch {
      status.value = -1
    }
  }

  // Busca dados de uma empresa com base no CpfCnpj
  const getDataWithIdentificationDocument = async (document: string): Promise<PeopleAliareType> => {
    try {
      const { data } = await empresasService.getPeopleByIdentification(document)
      return data
    } catch {
      //
    }
  }

  const getById = async (id: string): Promise<EmpresasType> => {
    status.value = 1
    try {
      const { data } = await empresasService.get(id)
      return data as EmpresasType
    } catch {
      //
    } finally {
      status.value = 2
    }
  }

  return {
    list,
    companiesNoContract,
    //Getters
    loading,
    // Actions
    create,
    update,
    getAll,
    getById,
    remove,
    getDataWithIdentificationDocument
  }
})

export default useEmpresasStore
